import { useState, useCallback, useEffect, useRef } from 'react';
import { YMaps, Map, Placemark, ZoomControl, GeolocationControl } from 'react-yandex-maps';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Button } from '@/components';
import Icon from '@/assets/icons';
import checkMobile from '@/lib/checkMobile';
import { SideShop } from './components';
import clsx from 'clsx';

import styles from './ShopsMap.module.scss';

const ShopsMap = ({
	shops = [],
	hiddenTabs,
	latitude: initialActivePin = '',
	isContact = false,
}) => {
	const mapRef = useRef(null);

	const shopsGroupedByLatitude = shops.reduce((acc, shop) => {
		const { latitude } = shop;
		if (acc[latitude]) {
			acc[latitude].push({ ...shop });
		} else {
			acc[latitude] = [{ ...shop }];
		}
		return acc;
	}, {});

	const [activePin, setActivePin] = useState(initialActivePin);
	const [sideOpen, setSideOpen] = useState(false);

	const onPinClick = useCallback(
		latitude => () => {
			setActivePin(latitude);
		},
		[],
	);

	const onCloseClick = useCallback(() => {
		setSideOpen(false);
		setActivePin('');
	}, []);

	useEffect(() => {
		if (activePin) {
			setSideOpen(true);
		}
	}, [activePin]);

	return (
		<YMaps>
			<div
				className={clsx('shopsMap', 'map', styles.shopsMap)}
				style={isContact ? { height: '100%' } : {}}
			>
				<div className={clsx({ [styles.open]: sideOpen }, styles.side)}>
					<Button
						classNames={{ general: styles.close, content: styles.closeButtonContent }}
						action='button'
						onClick={onCloseClick}
					>
						<Icon name='plus' />
					</Button>
					<ul className={styles.sideShops}>
						<TransitionGroup component={null}>
							{shopsGroupedByLatitude[activePin] &&
								shopsGroupedByLatitude[activePin].map(shop => {
									const { id } = shop;
									return (
										<CSSTransition key={id} timeout={100}>
											<li className={styles.sideShop}>
												<SideShop {...shop} />
											</li>
										</CSSTransition>
									);
								})}
						</TransitionGroup>
					</ul>
				</div>

				<Map
					defaultState={{ center: [53.902496, 27.561481], zoom: 12.2 }}
					className='map-wrapper'
					style={isContact ? { height: '100%' } : {}}
					instanceRef={ref => {
						if (ref) {
							mapRef.current = ref;
							// show all point on mobile
							if (window.innerWidth < 992) {
								ref.setCenter([53.902496, 27.561481], 11.7, {
									checkZoomRange: true,
								});
							}

							if (window.innerWidth < 768) {
								ref.setCenter([53.902496, 27.561481], 11, {
									checkZoomRange: true,
								});
							}

							if (window.innerWidth < 480) {
								ref.setCenter([53.902496, 27.561481], 10.6, {
									checkZoomRange: true,
								});
							}
							if (window.innerWidth < 370) {
								ref.setCenter([53.902496, 27.561481], 10.4, {
									checkZoomRange: true,
								});
							}

							// disable drag of map by touch on mobile
							if (checkMobile()) {
								ref.behaviors.disable('drag');
							}
							// disable zoom by scroll on desktop
							ref.behaviors.disable('scrollZoom');
							ref.events.add('sizechange', () => {
								if (checkMobile()) {
									ref.behaviors.disable('drag');
								} else {
									ref.behaviors.enable('drag');
								}
							});
						}
					}}
				>
					{Object.keys(shopsGroupedByLatitude).map(lat => {
						return (
							<Placemark
								key={lat}
								geometry={{
									type: 'Point',
									coordinates: [+lat, +shopsGroupedByLatitude[lat][0].longitude],
								}}
								defaultOptions={{
									iconImageSize: [28, 35],
									iconImageOffset: [-14, -17],
								}}
								options={
									activePin === lat
										? {
												iconLayout: 'default#image',
												iconImageHref: '/images/map/pin-active.webp',
										  }
										: {
												iconLayout: 'default#image',
												iconImageHref: '/images/map/pin.webp',
										  }
								}
								onClick={onPinClick(lat)}
							/>
						);
					})}

					<ZoomControl options={{ position: { left: 'auto', right: '20px', top: '35vh' } }} />

					<GeolocationControl
						options={{
							position: { left: 'auto', right: '20px', top: 'calc(35vh - 40px)' },
						}}
					/>
				</Map>
			</div>
		</YMaps>
	);
};

export default ShopsMap;
